import React from 'react';
import { motion } from 'framer-motion';
import { Section, FadeIn } from './ui';
import { CheckIcon, XIcon } from 'lucide-react';
type BeforeAfterProps = {
  eyebrow?: string;
  headline?: React.ReactNode;
  subhead?: string;
  image?: string;
  alt?: string;
  beforeItems?: string[];
  afterItems?: string[];
  afterLabel?: string;
};
const DEFAULTS = {
  eyebrow: 'The Offsite+ Difference',
  headline: <>
      The same group. Two completely{' '}
      <span className="text-brand-coral" data-id="element-228">different experiences</span>.
    </>,
  subhead: "Offsite+ replaces forgettable ballroom logistics with structured, engaging, branded experiences — and it's all visible to your team in real time.",
  image: 'https://cdn.magicpatterns.com/uploads/v3XbqzDcsd3QHNNJfgFUj6/Offsite_Plus.webp',
  alt: 'Before and after: traditional boring offsite with paper agendas and survey clipboards versus an Offsite+ retreat with live dashboards, branded event hub, and engaged collaborative groups',
  beforeItems: ['Generic ballroom keynote', 'Paper agendas + lukewarm coffee', 'Buffet line tension and small talk', 'Survey on a clipboard at the exit'],
  afterItems: ['Live dashboard visible to the whole team', "Branded event hub in every attendee's pocket", 'Intentional groupings around real conversations', 'Real-time engagement, intake, and post-event report'],
  afterLabel: 'After · with Offsite+'
};
export function BeforeAfter(props: BeforeAfterProps = {}) {
  const eyebrow = props.eyebrow ?? DEFAULTS.eyebrow;
  const headline = props.headline ?? DEFAULTS.headline;
  const subhead = props.subhead ?? DEFAULTS.subhead;
  const image = props.image ?? DEFAULTS.image;
  const alt = props.alt ?? DEFAULTS.alt;
  const beforeItems = props.beforeItems ?? DEFAULTS.beforeItems;
  const afterItems = props.afterItems ?? DEFAULTS.afterItems;
  const afterLabel = props.afterLabel ?? DEFAULTS.afterLabel;
  return <Section className="bg-white relative overflow-hidden" data-id="element-229">
      <div className="absolute inset-0 pointer-events-none" data-id="element-230">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-coral/[0.05] rounded-full blur-3xl" data-id="element-231" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-brand-teal/[0.06] rounded-full blur-3xl" data-id="element-232" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center mb-12" data-id="element-233">
        <FadeIn data-id="element-234">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm" data-id="element-235">
            <span className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-236">
              {eyebrow}
            </span>
          </div>
        </FadeIn>
        <FadeIn delay={0.05} data-id="element-237">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy leading-tight mb-5 text-balance" data-id="element-238">
            {headline}
          </h2>
        </FadeIn>
        <FadeIn delay={0.15} data-id="element-239">
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed" data-id="element-240">
            {subhead}
          </p>
        </FadeIn>
      </div>

      <FadeIn delay={0.2} data-id="element-241">
        <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200 bg-white" data-id="element-242">
          <img src={image} alt={alt} className="w-full h-auto block" data-id="element-243" />

          {/* Animated reveal divider */}
          <motion.div aria-hidden initial={{
          scaleY: 0
        }} whileInView={{
          scaleY: 1
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.9,
          delay: 0.3,
          ease: [0.65, 0, 0.35, 1]
        }} style={{
          transformOrigin: 'top'
        }} className="absolute left-1/2 top-0 bottom-0 w-px bg-white/80 hidden md:block" data-id="element-244" />
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mt-10" data-id="element-245">
        <FadeIn delay={0.3} data-id="element-246">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-id="element-247">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-4" data-id="element-248">
              <XIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-249" /> Before
            </div>
            <ul className="space-y-2.5 text-slate-600" data-id="element-250">
              {beforeItems.map(t => <li key={t} className="flex items-start gap-2 text-sm" data-id="element-251">
                  <span className="text-slate-400 mt-1" data-id="element-252">—</span>
                  {t}
                </li>)}
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.4} data-id="element-253">
          <div className="rounded-2xl border-2 border-brand-coral/20 bg-brand-coral/5 p-6" data-id="element-254">
            <div className="inline-flex items-center gap-2 bg-brand-coral text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-4 shadow-lg shadow-brand-coral/30" data-id="element-255">
              <CheckIcon className="w-3.5 h-3.5" data-id="element-256" /> {afterLabel}
            </div>
            <ul className="space-y-2.5 text-slate-700" data-id="element-257">
              {afterItems.map(t => <li key={t} className="flex items-start gap-2 text-sm" data-id="element-258">
                  <CheckIcon className="w-4 h-4 text-brand-coral mt-0.5 shrink-0" data-id="element-259" />
                  {t}
                </li>)}
            </ul>
          </div>
        </FadeIn>
      </div>
    </Section>;
}